import * as _ from "lodash";
import { FilterChoices } from "processInstanceList/components/ProcessInstanceListSearch";
import qs, { ParsedQs } from "qs";
import { generatePath, matchPath } from "react-router";

import { ProcessInstanceStateFilter } from "../model/ProcessInstanceStateFilter";
import { FilterCategory, ListState } from "./ListState";
import { getCountsTimeLimitDaysFromStore } from "./CountsTimeLimitDaysStore";

export enum ProcessListType {
    "Process" = "Process",
    "Subprocess" = "Subprocess"
}

export const pageFilterKeyName = "page";

const getProcessListURLBase = () => {
    const pattern = `/:base/:processListType?/*`;
    const matchedPath = matchPath(pattern, window.location.pathname);
    const base = matchedPath?.params.base ?? ProcessListType.Process;
    if (base.toLowerCase() === ProcessListType.Process.toLowerCase() || base.toLowerCase() === ProcessListType.Subprocess.toLowerCase()) {
        return `/${base}/List`;
    }
    const processListType = matchedPath?.params.processListType ?? ProcessListType.Process;
    return `/${base}/${processListType}/List`;
};

export const basename = getProcessListURLBase();

export const processListURLPattern = `/:base?/:processType/List/:environmentName?/:agentGroupName?/:instances?`;

export const getProcessListURL = (): IProcessListURL => matchPath({ path: processListURLPattern }, window.location.pathname)?.params as IProcessListURL || { processType: "process" };

export const getProcessListType = (): ProcessListType => getProcessListURL().processType;

export const isSubprocessList = (): boolean => getProcessListType().toLowerCase() === ProcessListType.Subprocess.toLowerCase();

const getSearchParametersFromString = (searchString: string): ProcessListSearchParams & InstanceListSearchParameters => {
    const searchParams = qs.parse(searchString);
    const page = parseInt(searchParams[pageFilterKeyName] as string, 10);
    const pageSize = parseInt(searchParams["pageSize"] as string, 10);
    const highlightedProcess = parseInt(searchParams["highlightProcess"] as string, 10);

    const filterCategoryString = searchParams["filterCategory"] as string;
    const filterCategory: string | undefined = Object.values(FilterCategory).find((x) => x === filterCategoryString);

    const filterText: IFilterType = searchParams["filterText"] as string || null;
    const processGuids: IFilterType = searchParams["processGuids"] as string || null;
    let processGuidsArray: string[] = [];
    if (processGuids) {
        if (isArrayOfStrings(processGuids)) {
            processGuidsArray = processGuids;
        } else if (typeof processGuids === 'string') {
            processGuidsArray.push(processGuids)
        }
    }

    const startTime: IFilterType = searchParams["startTime"] as string || null;
    const endTime: IFilterType = searchParams["endTime"] as string || null;

    const stateSearchString = searchParams["instanceListStateFilter"] as string;
    const stateFilter: ProcessInstanceStateFilter | undefined = Object.values(ProcessInstanceStateFilter).find((x) => x === stateSearchString);

    return {
        page: !isNaN(page) ? page : undefined,
        pageSize: !isNaN(pageSize) ? pageSize : undefined,
        sortBy: searchParams["sortBy"] as string ?? null,
        desc: searchParams["desc"] && searchParams["desc"] === "true",
        highlightProcess: !isNaN(highlightedProcess) ? highlightedProcess : undefined,
        filterCategory: filterCategory || FilterCategory.Text,
        filterText: filterCategory && filterText,
        instanceListStateFilter: stateFilter,
        processGuids: processGuidsArray,
        endTime: endTime,
        startTime: startTime
    };
};

function isArrayOfStrings(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === "string");
}
export function getAgentGroupEnvironmentFromPath(): AgentGroupEnvironment {
    const processListURL = getProcessListURL();
    const agentGroupName = processListURL.agentGroupName ?? "Default"; // if no agent group is passed we do not want to keep that as null. We do checks after we recieve all possilbe agent groups in FetchActions.ts
    const environmentName = processListURL.environmentName ?? "Default";
    return { environmentName: environmentName, agentGroupName: agentGroupName };
}

export function setAgentGroupToPath(environmentName: string, agentGroupName: string, navigate: any) {
    const processListURL = getProcessListURL();
    if (processListURL.environmentName !== environmentName
        || processListURL.agentGroupName !== agentGroupName) {

        processListURL.agentGroupName = agentGroupName;
        processListURL.environmentName = environmentName;
        const newPathname = generatePath<string>("/:environmentName/:agentGroupName", {
            environmentName: processListURL.environmentName,
            agentGroupName: processListURL.agentGroupName,
        });

        const currentSearchParams = new URLSearchParams(window.location.search);
        if (currentSearchParams.get("page") !== null) {
            currentSearchParams.set("page", "1");
        }

        navigate(newPathname + "?" + currentSearchParams.toString());
    }
}

export const resetInstanceFilters = (processGuids: string[], navigate: any) => {
    const processListURL = getProcessListURL();
    const newPathname = generatePath<string>("/:environmentName/:agentGroupName/:instances", {
        environmentName: processListURL.environmentName,
        agentGroupName: processListURL.agentGroupName,
        instances: "instances"
    });
    let newSearch = qs.stringify(Object.assign({}, { processGuids: processGuids }), { skipNulls: true, indices: false });
    newSearch = newSearch !== "" ? ("?" + newSearch) : "";
    navigate(newPathname + newSearch, { replace: true });
}

export function getInstancePath(processGuids: string[] | undefined, stateFilter?: ProcessInstanceStateFilter): string {
    return fetchInstancePath(processGuids, stateFilter)
}

const getExistingSearchStringified = (): string | null => {
    const existingSearch = qs.stringify(getSearchParameters(), { skipNulls: true, indices: false });
    if (existingSearch) {
        return encodeURIComponent(existingSearch);
    }

    return null;
};

const fetchInstancePath = (processGuids: string[] | undefined, stateFilter?: ProcessInstanceStateFilter) => {
    const newSearch = qs.stringify(Object.assign({}, { processGuids: processGuids }, { instanceListStateFilter: stateFilter }, { search: getExistingSearchStringified() }), { skipNulls: true, indices: false });
    return "instances" + "?" + newSearch;
}

export function getProcessPath(processId: number, isSubprocess: boolean, environment?: string, agentGroup?: string, listUrl?: IProcessListURL): string {
    const processListURL = listUrl ?? getProcessListURL();

    const newPathname = generatePath<string>(processListURLPattern, {
        base: processListURL.base,
        processType: isSubprocess ? ProcessListType.Subprocess : ProcessListType.Process,
        environmentName: environment || processListURL.environmentName,
        agentGroupName: agentGroup || processListURL.agentGroupName
    });

    return newPathname + "?highlightProcess=" + processId;
}

export function getSearchParameters(searchOverride?: string): ProcessListSearchParams & InstanceListSearchParameters {
    // When provided, searchOverride is used instead of window.location.search to avoid reading stale browser state
    const search = searchOverride ?? location.search;
    const searchString = search && search.substring(1); // strip leading '?'
    return getSearchParametersFromString(searchString);
}

export function replaceSearchParameters(parameters: ProcessListSearchParams | InstanceListSearchParameters, navigate: any) {
    const newSearchString = qs.stringify(parameters, { skipNulls: true, arrayFormat: "repeat" });
    navigate("?" + newSearchString, { replace: true });
}

export function getInitialSearchState(): Partial<ListState> {
    const initialStateFromUri = getSearchParameters();
    let countsTimeLimitDays = getCountsTimeLimitDaysFromStore();

    if (filterIsOnCountsAndOlderThanCurrentTimeLimit(initialStateFromUri.filterCategory, initialStateFromUri.filterText, countsTimeLimitDays)) {
        countsTimeLimitDays = 0;
    }

    return {
        countsTimeLimitDays: countsTimeLimitDays,
    };
}

export function filterIsOnCountsAndOlderThanCurrentTimeLimit(category: FilterCategory | null | string, filter: IFilterType | undefined, currentTimeLimit: number) {
    if (category === FilterCategory.ErrorsSince || category === FilterCategory.ExecutionsSince && currentTimeLimit !== 0) {
        const filterDays = parseInt(filter as string, 10);
        return !_.isNaN(filterDays) && filterDays > currentTimeLimit;
    }
    return false;
}

export const getNewUrlState = (newValues: Partial<ProcessListSearchParams>): ProcessListSearchParams => {
    const currentParams = getSearchParameters();
    return _.merge({
        desc: currentParams.desc,
        highlightProcess: null,
        page: currentParams.page,
        pageSize: currentParams.pageSize,
        sortBy: currentParams.sortBy,
        filterCategory: currentParams.filterCategory,
        filterText: currentParams.filterText,
        instanceListStateFilter: null
    }, newValues);
}

export const removePageFromSearchParameters = (urlSearchParams: URLSearchParams) => {
    if (urlSearchParams.toString() === "") {
        return "";
    }

    // If encoded process list filters are present (via 'search' param),
    // decode and use them instead of passing through instance-list-specific params.
    if (urlSearchParams.has("search")) {
        const encodedSearch = urlSearchParams.get("search");
        if (encodedSearch) {
            const decoded = decodeURIComponent(encodedSearch);
            const originalParams = new URLSearchParams(decoded);
            originalParams.delete(pageFilterKeyName);
            return originalParams.toString() ? `?${originalParams.toString()}` : "";
        }
        return "";
    }

    if (urlSearchParams.has(pageFilterKeyName)) {
        const searchClone = new URLSearchParams(urlSearchParams);
        searchClone.delete(pageFilterKeyName);
        return `?${searchClone.toString()}`
    }

    return `?${urlSearchParams.toString()}`;
}

export interface ProcessListSearchParams {
    page?: number | null;
    pageSize?: number | null;
    sortBy: IFilterType;
    desc?: boolean | null | string;
    filterText?: IFilterType;
    highlightProcess?: number | null;
    filterCategory: FilterCategory | FilterChoices | string | null;
}

export interface InstanceListSearchParameters {
    instanceListStateFilter: ProcessInstanceStateFilter | undefined;
    processGuids: string[];
    startTime?: IFilterType;
    endTime?: IFilterType;
}

export type IFilterType = string | ParsedQs | (string | ParsedQs)[] | null;

export interface AgentGroupEnvironment {
    agentGroupName: string;
    environmentName: string;
}

export interface IProcessListURL extends AgentGroupEnvironment, Record<string, string | undefined> {
    base?: string;
    action: string;
    id: string;
    processType: ProcessListType;
    instances?: string;
}
